import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, cn } from "@nexus/ui";
import { toast } from "sonner";
import { UPLOAD_PURPOSE_SCRIPT_REVIEW_SCRIPT } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { opportunitySetsQueryOptions } from "@/lib/queries/opportunity-sets.queries";
import {
  scriptsForSetQueryOptions,
  scriptReviewStatusQueryOptions,
  scriptReviewListQueryOptions,
  scriptReviewKeys,
} from "@/lib/queries/script-review.queries";
import {
  runScriptReviewUploadFlow,
  createScriptReview,
  type ScriptMatchResult,
} from "@/features/script-review/api/script-review.api";
import {
  useScriptReviewUpload,
  type ScriptReviewUpload,
} from "@/features/script-review/hooks/use-script-review-upload";
import { useScriptMatch } from "@/features/script-review/hooks/use-script-match";
import {
  trackScriptReviewCreated,
  trackScriptReviewMatchResolved,
} from "@/lib/analytics-events";
import { ScriptReviewPromptsSection } from "@/features/script-review/components/ScriptReviewPromptsSection";
import { ScriptReviewRejectionsSection } from "@/features/script-review/components/ScriptReviewRejectionsSection";
import { translateScriptReviewStatusLabel } from "@/features/script-review/lib/script-review-status";
import { prepareScriptFile } from "@/features/script-review/lib/spreadsheet-to-csv";
import { toastScriptReviewError } from "@/features/script-review/lib/toast-script-review-error";
import { GENERIC_API_ERROR_I18N_KEY } from "@/lib/api-client";

export interface ScriptReviewHubPageProps {
  onOpenReview: (reviewId: string) => void;
}

/** What the vendor was given, and therefore what the AI judges the video against. */
type ScriptMode = "set" | "upload" | "iteration" | "clean";

const SCRIPT_MODES: readonly ScriptMode[] = ["set", "upload", "iteration", "clean"];

/** Normalize a script file's content type by extension (browser MIME for .csv is unreliable). */
function scriptContentType(f: File): string {
  const name = f.name.toLowerCase();
  if (name.endsWith(".csv")) return "text/csv";
  if (name.endsWith(".txt")) return "text/plain";
  return f.type || "text/plain";
}

/** Upload state for a picked video: a bar while it moves, the filename when done. */
function UploadStatus({ upload }: { upload: ScriptReviewUpload }) {
  const { t } = useTranslation();
  if (!upload.file) return null;

  const sizeMb = (upload.file.size / (1024 * 1024)).toFixed(1);
  if (upload.state === "failed") {
    return (
      <span className="text-xs text-destructive">
        {upload.error ?? t("scriptReview.upload.failed", "Upload failed — pick the file again.")}
      </span>
    );
  }
  if (upload.state === "ready") {
    return (
      <span className="text-xs text-muted-foreground">
        {upload.file.name} · {sizeMb} MB · {t("scriptReview.upload.ready", "uploaded")}
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-200"
          style={{ width: `${Math.max(upload.progress, 4)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">
        {upload.state === "confirming"
          ? t("scriptReview.upload.confirming", "Finishing up…")
          : t("scriptReview.upload.uploading", "Uploading {{name}} — {{percent}}%", {
              name: upload.file.name,
              percent: upload.progress,
            })}
      </span>
    </div>
  );
}

export function ScriptReviewHubPage({ onOpenReview }: ScriptReviewHubPageProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const qc = useQueryClient();

  // The video and the reference clip upload as soon as they're picked — the AI
  // can't transcribe what isn't in storage yet, and the match starts the moment
  // a set is chosen. The script FILE still uploads on submit (nothing waits on it).
  const videoUpload = useScriptReviewUpload({ tenantId: activeTenantId, t });
  const referenceUpload = useScriptReviewUpload({ tenantId: activeTenantId, t });
  const [scriptMode, setScriptMode] = useState<ScriptMode>("set");
  const [setId, setSetId] = useState("");
  const [scriptId, setScriptId] = useState("");
  const [scriptFile, setScriptFile] = useState<File | null>(null);
  const [brief, setBrief] = useState("");
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [pollStartedAt, setPollStartedAt] = useState(0);

  // Auto-match: the AI's suggestion, and whether the admin has taken the wheel.
  const [matchBadge, setMatchBadge] = useState<ScriptMatchResult | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [scriptPickedByUser, setScriptPickedByUser] = useState(false);
  /** The (video, set) pair we last asked about — stops the effect re-firing. */
  const lastMatchKeyRef = useRef<string | null>(null);
  const prevStatusRef = useRef<string | undefined>(undefined);
  const failedToastReviewRef = useRef<string | null>(null);

  const setsQuery = useQuery(opportunitySetsQueryOptions(activeTenantId));
  const scriptsQuery = useQuery(scriptsForSetQueryOptions(setId || null));
  const statusQuery = useQuery(
    scriptReviewStatusQueryOptions(activeTenantId, reviewId ?? "", pollStartedAt),
  );

  useEffect(() => {
    const status = statusQuery.data?.status;
    if (!status || !reviewId) return;
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (
      status === "FAILED" &&
      prev &&
      prev !== "FAILED" &&
      failedToastReviewRef.current !== reviewId
    ) {
      failedToastReviewRef.current = reviewId;
      toast.error(t(GENERIC_API_ERROR_I18N_KEY));
    }
  }, [statusQuery.data?.status, reviewId, t]);
  const reviewsQuery = useQuery(scriptReviewListQueryOptions(activeTenantId));

  /** Handed the verdict by the match hook; drives the inline badge / error. */
  const onMatchResult = useCallback(
    (result: ScriptMatchResult | null) => {
      // The admin may have picked a script while we were thinking — never
      // yank a deliberate choice out from under them.
      if (scriptPickedByUser) return;
      if (result?.matched && result.scriptId) {
        setScriptId(result.scriptId);
        setMatchBadge(result);
        setMatchError(null);
        trackScriptReviewMatchResolved({
          opportunity_set_id: setId,
          outcome: "matched",
          confidence: result.confidence,
          script_id: result.scriptId,
        });
        return;
      }
      setMatchBadge(null);
      setMatchError(
        t(
          "scriptReview.match.noMatch",
          "Couldn't match this video automatically — please pick the script below.",
        ),
      );
      trackScriptReviewMatchResolved({
        opportunity_set_id: setId,
        outcome: result ? "no_match" : "error",
        confidence: result?.confidence,
      });
    },
    [scriptPickedByUser, setId, t],
  );

  const match = useScriptMatch(onMatchResult);
  const matchMutate = match.mutate;

  // Fire the auto-match once the video is stored AND a set is chosen — whichever
  // lands second. Keyed on the pair so switching sets re-asks, but a re-render
  // never does.
  useEffect(() => {
    if (scriptMode !== "set") return;
    if (!activeTenantId || !setId) return;
    if (videoUpload.state !== "ready" || !videoUpload.uploadId) return;
    if (scriptPickedByUser) return;

    const key = `${videoUpload.uploadId}:${setId}`;
    if (lastMatchKeyRef.current === key) return;
    lastMatchKeyRef.current = key;

    setMatchError(null);
    matchMutate({ tenantId: activeTenantId, uploadId: videoUpload.uploadId, setId });
  }, [
    scriptMode,
    activeTenantId,
    setId,
    videoUpload.state,
    videoUpload.uploadId,
    scriptPickedByUser,
    matchMutate,
  ]);

  const run = useMutation({
    mutationFn: async () => {
      if (!activeTenantId) throw new Error(t("scriptReview.errors.selectVideo", "Select a video"));
      if (videoUpload.state !== "ready" || !videoUpload.uploadId) {
        throw new Error(
          videoUpload.state === "uploading" || videoUpload.state === "confirming"
            ? t("scriptReview.errors.videoStillUploading", "Wait for the video to finish uploading")
            : t("scriptReview.errors.selectVideo", "Select a video"),
        );
      }
      if (scriptMode === "set" && (!setId || !scriptId)) {
        throw new Error(
          t("scriptReview.errors.selectSetAndScript", "Select an opportunity set and a script"),
        );
      }
      if (scriptMode === "upload" && !scriptFile) {
        throw new Error(t("scriptReview.errors.uploadScript", "Upload a script file"));
      }
      if (scriptMode === "iteration" && (referenceUpload.state !== "ready" || brief.trim().length < 4)) {
        throw new Error(
          t(
            "scriptReview.errors.iterationInputs",
            "Upload the reference video and describe the change that was briefed",
          ),
        );
      }
      // The video (and reference) went up when they were picked — reuse them.
      const uploadId = videoUpload.uploadId;
      if (scriptMode === "clean") return createScriptReview({ uploadId, mode: "CLEAN" });
      if (scriptMode === "upload") {
        // A spreadsheet becomes CSV here — see prepareScriptFile for why.
        const preparedScript = await prepareScriptFile(scriptFile!);
        const scriptUploadId = await runScriptReviewUploadFlow({
          tenantId: activeTenantId,
          file: preparedScript,
          purpose: UPLOAD_PURPOSE_SCRIPT_REVIEW_SCRIPT,
          contentType: scriptContentType(preparedScript),
        });
        return createScriptReview({ uploadId, scriptUploadId });
      }
      if (scriptMode === "iteration") {
        return createScriptReview({
          uploadId,
          referenceUploadId: referenceUpload.uploadId!,
          brief: brief.trim(),
        });
      }
      return createScriptReview({ uploadId, setId, scriptId });
    },
    onSuccess: ({ reviewId: id }) => {
      setReviewId(id);
      setPollStartedAt(Date.now());
      trackScriptReviewCreated({
        review_id: id,
        script_source: scriptMode,
        opportunity_set_id: scriptMode === "set" ? setId : undefined,
        script_id: scriptMode === "set" ? scriptId : undefined,
        video_size_bytes: videoUpload.file?.size ?? 0,
        script_file_type:
          scriptMode === "upload" && scriptFile
            ? scriptFile.name.split(".").pop()?.toLowerCase()
            : undefined,
        reference_video_size_bytes:
          scriptMode === "iteration" ? (referenceUpload.file?.size ?? 0) : undefined,
        brief_length: scriptMode === "iteration" ? brief.trim().length : undefined,
        // Did they run with the AI's pick, or override it? The acceptance rate
        // is the only honest read on whether matching is any good.
        script_auto_matched:
          scriptMode === "set" && matchBadge ? matchBadge.scriptId === scriptId : undefined,
      });
      void qc.invalidateQueries({ queryKey: scriptReviewKeys.list(activeTenantId || "__none__") });
    },
    onError: (e) => toastScriptReviewError(e),
  });

  const status = statusQuery.data?.status;
  const canRun =
    videoUpload.state === "ready" &&
    !run.isPending &&
    (scriptMode === "clean" ||
      (scriptMode === "set"
        ? !!setId && !!scriptId
        : scriptMode === "iteration"
          ? referenceUpload.state === "ready" && brief.trim().length >= 4
          : !!scriptFile));

  /** A different set means a different shortlist — drop the old answer. */
  function onSelectSet(nextSetId: string) {
    setSetId(nextSetId);
    setScriptId("");
    setScriptPickedByUser(false);
    setMatchBadge(null);
    setMatchError(null);
  }

  /** A manual pick wins over the AI, and stops it from firing again. */
  function onSelectScript(nextScriptId: string) {
    setScriptId(nextScriptId);
    setScriptPickedByUser(true);
    setMatchBadge(null);
  }

  function resetForNext() {
    setReviewId(null);
    setPollStartedAt(0);
    prevStatusRef.current = undefined;
    failedToastReviewRef.current = null;
    videoUpload.reset();
    referenceUpload.reset();
    setScriptId("");
    setScriptFile(null);
    setBrief("");
    setScriptPickedByUser(false);
    setMatchBadge(null);
    setMatchError(null);
    lastMatchKeyRef.current = null;
  }

  return (
    <div className="container mx-auto flex max-w-5xl flex-col gap-8 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("scriptReview.title", "Pre Flight – Scripts")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "scriptReview.subtitle",
            "Review a finished video against its production script and share a timestamped report with the partner.",
          )}
        </p>
      </header>

      {/* ── Input card ─────────────────────────────────────────── */}
      <section className="flex flex-col gap-5 rounded-lg border border-border bg-card p-6">
        {/* 1. Video */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">{t("scriptReview.video", "1. Upload video")}</label>
          <input
            type="file"
            accept="video/mp4,video/quicktime"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) videoUpload.start(f);
              else videoUpload.reset();
            }}
            className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
          />
          <UploadStatus upload={videoUpload} />
        </div>

        {/* 2. What the vendor was given — a script, or a video to iterate on */}
        <div className="flex flex-col gap-3">
          <label className="text-sm font-medium">
            {t("scriptReview.scriptStep", "2. What the partner was briefed with")}
          </label>

          <div className="inline-flex w-fit rounded-md border border-border bg-muted/40 p-0.5 text-sm">
            {SCRIPT_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setScriptMode(mode)}
                className={cn(
                  "rounded px-3 py-1 transition-colors",
                  scriptMode === mode
                    ? "bg-background font-medium shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {mode === "set"
                  ? t("scriptReview.fromSet", "From opportunity set")
                  : mode === "upload"
                    ? t("scriptReview.uploadOwn", "Upload your own")
                    : mode === "iteration"
                      ? t("scriptReview.iteration.toggle", "Iterate on a winning video")
                      : t("scriptReview.clean.toggle", "Clean upload")}
              </button>
            ))}
          </div>

          {scriptMode === "clean" ? (
            <span className="text-xs text-muted-foreground">
              {t(
                "scriptReview.clean.hint",
                "Stores the video with no AI review — it opens on the timeline with no comments, ready for your team's notes.",
              )}
            </span>
          ) : scriptMode === "iteration" ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">
                  {t("scriptReview.iteration.referenceLabel", "Reference video (the one that worked)")}
                </label>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) referenceUpload.start(f);
                    else referenceUpload.reset();
                  }}
                  className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
                />
                {referenceUpload.file ? (
                  <UploadStatus upload={referenceUpload} />
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t(
                      "scriptReview.iteration.referenceHint",
                      "The proven video the new cut should live up to.",
                    )}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">
                  {t("scriptReview.iteration.briefLabel", "Change brief")}
                </label>
                <textarea
                  rows={3}
                  maxLength={5000}
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder={t(
                    "scriptReview.iteration.briefPlaceholder",
                    "e.g. Double down on the hook — keep everything else as close to the original as possible.",
                  )}
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <span className="text-xs text-muted-foreground">
                  {t(
                    "scriptReview.iteration.briefHint",
                    "What the partner was asked to change. The AI checks the change landed — and that nothing that made the original work was lost.",
                  )}
                </span>
              </div>
            </div>
          ) : scriptMode === "set" ? (
            <div className="flex flex-col gap-3">
              <select
                value={setId}
                onChange={(e) => onSelectSet(e.target.value)}
                disabled={setsQuery.isLoading}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">
                  {setsQuery.isLoading
                    ? t("scriptReview.hub.loading", "Loading…")
                    : t("scriptReview.hub.selectSet", "Select a set…")}
                </option>
                {(setsQuery.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {/* The dropdown stays live while the AI thinks — the match is a
                  shortcut, never a gate. */}
              <select
                value={scriptId}
                onChange={(e) => onSelectScript(e.target.value)}
                disabled={!setId || scriptsQuery.isLoading}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm disabled:opacity-50"
              >
                <option value="">
                  {!setId
                    ? t("scriptReview.hub.selectSetFirst", "Select a set first")
                    : scriptsQuery.isLoading
                      ? t("scriptReview.hub.loading", "Loading…")
                      : t("scriptReview.hub.selectScript", "Select a script…")}
                </option>
                {(scriptsQuery.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {[s.opportunityTitle, s.concept, s.hook].filter(Boolean).join(" — ").slice(0, 90) || s.id}
                  </option>
                ))}
              </select>

              {match.isPending && (
                <span className="text-xs text-muted-foreground">
                  {t("scriptReview.match.finding", "Watching the video to find its script…")}
                </span>
              )}
              {!match.isPending && matchBadge?.matched && matchBadge.scriptId === scriptId && (
                <div className="flex flex-col gap-1 rounded-md border border-sky-500/30 bg-sky-500/10 p-2">
                  <span className="text-xs font-medium text-sky-700 dark:text-sky-300">
                    {t("scriptReview.match.badge", "AI matched — change it above if this is wrong")}
                  </span>
                  {matchBadge.reasoning && (
                    <span className="text-xs text-muted-foreground">{matchBadge.reasoning}</span>
                  )}
                </div>
              )}
              {!match.isPending && matchError && (
                <span className="text-xs text-muted-foreground">{matchError}</span>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <input
                type="file"
                accept=".csv,.txt,.xlsx,.xlsm,.xls,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={(e) => setScriptFile(e.target.files?.[0] ?? null)}
                className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
              />
              <span className="text-xs text-muted-foreground">
                {scriptFile
                  ? `${scriptFile.name} · ${(scriptFile.size / 1024).toFixed(0)} KB`
                  : t(
                      "scriptReview.uploadHint",
                      "Excel, CSV, or TXT — we'll parse it into scenes to compare the video against.",
                    )}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={() => run.mutate()} disabled={!canRun}>
            {run.isPending
              ? t("scriptReview.starting", "Starting…")
              : t("scriptReview.run", "Run pre-flight")}
          </Button>
          {run.isPending && (
            <span className="text-sm text-muted-foreground">
              {t("scriptReview.hub.queuingAnalysis", "Uploading & queuing analysis…")}
            </span>
          )}
        </div>
      </section>

      {/* ── Live status / result ───────────────────────────────── */}
      {reviewId && (
        <section className="flex flex-col gap-3 rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t("scriptReview.statusLabel", "Status")}:{" "}
              {translateScriptReviewStatusLabel(t, status ?? "QUEUED")}
            </span>
            {status && status !== "COMPLETED" && status !== "FAILED" && (
              <span className="text-xs text-muted-foreground">
                {t(
                  "scriptReview.hub.analysisDuration",
                  "This can take a few minutes — transcript + 3 AI passes.",
                )}
              </span>
            )}
          </div>

          {status === "COMPLETED" && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {t("scriptReview.hub.commentsFound", {
                  count: statusQuery.data?.commentCount ?? 0,
                  defaultValue:
                    "{{count}} comments found. Open the report to watch the video on the timeline, collaborate on comments, and share it with the partner.",
                })}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" onClick={() => onOpenReview(reviewId)}>
                  {t("scriptReview.viewReport", "View report")}
                </Button>
                <Button variant="ghost" onClick={resetForNext}>
                  {t("scriptReview.runAnother", "Run another")}
                </Button>
              </div>
            </div>
          )}

          {status === "FAILED" && (
            <p className="text-sm text-muted-foreground">
              {t("scriptReview.hub.reviewFailed", "The review failed. Please try again.")}
            </p>
          )}
        </section>
      )}

      {/* ── History ─────────────────────────────────────────────── */}
      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-medium text-muted-foreground">
          {t("scriptReview.recent", "Recent reviews")}
        </h2>
        <div className="overflow-hidden rounded-lg border border-border">
          {(reviewsQuery.data?.items ?? []).length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {reviewsQuery.isLoading
                ? t("scriptReview.hub.loading", "Loading…")
                : t("scriptReview.hub.noReviews", "No reviews yet.")}
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">
                    {t("scriptReview.history.created", "Created")}
                  </th>
                  <th className="px-4 py-2 font-medium">
                    {t("scriptReview.history.status", "Status")}
                  </th>
                  <th className="px-4 py-2 font-medium">
                    {t("scriptReview.history.comments", "Comments")}
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {(reviewsQuery.data?.items ?? []).map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-2">
                        {new Date(r.createdAt).toLocaleString()}
                        {r.version > 1 && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {t("scriptReview.version.badge", "v{{n}}", { n: r.version })}
                          </span>
                        )}
                        {r.mode === "ITERATION" && (
                          <span className="rounded border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-xs font-medium text-sky-700 dark:text-sky-300">
                            {t("scriptReview.iteration.badge", "Iteration")}
                          </span>
                        )}
                        {r.mode === "CLEAN" && (
                          <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                            {t("scriptReview.clean.badge", "Clean")}
                          </span>
                        )}
                        {r.readyForClientAt && (
                          <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                            {t("scriptReview.readiness.badge", "Ready")}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2">{translateScriptReviewStatusLabel(t, r.status)}</td>
                    <td className="px-4 py-2">{r.commentCount}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        className="text-primary underline"
                        onClick={() => onOpenReview(r.id)}
                      >
                        {t("scriptReview.history.open", "Open")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* AI pipeline prompts — global Prompts system, still SUPER_TENANT-only */}
      {(isSuperTenant || perms.PROMPTS_LIST.isOn) && <ScriptReviewPromptsSection />}

      {/* Prompt-improvement tickets — STRICTLY Adge admins (suggestions edit
          GLOBAL prompts), unlike the prompts section's grantable fallback. */}
      {isSuperTenant && <ScriptReviewRejectionsSection />}
    </div>
  );
}
